import { apiBlob, apiClient, apiClientPaginated } from '@/lib/api-client';
import type {
  ProductMasterListItem,
  ProductMasterDetail,
  ProductMasterSummary,
  SetMasterLocationsPayload,
  SetMasterLocationsResult,
  StorePriceResult,
  MasterConflict,
  MasterConflictType,
  ConflictAction,
  CreateProductMasterPayload,
  UpdateProductMasterPayload,
} from '@/types/product-master';

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

/** The master product list and what each store sells from it (`/product-masters`). */
export const productMasterService = {
  list(params: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    is_active?: 'true' | 'false';
    /** 'false' = products that are not variants (one row per product). */
    is_variant?: 'true' | 'false';
  }) {
    return apiClientPaginated<ProductMasterListItem[]>(`/product-masters${buildQuery(params)}`);
  },

  summary() {
    return apiClient<ProductMasterSummary>('/product-masters/summary');
  },

  /** The master with one row per store: sells it, stock, price, own price. */
  getById(id: string) {
    return apiClient<ProductMasterDetail>(`/product-masters/${id}`);
  },

  /** Owner only. No store sells the new master until it is enabled somewhere. */
  create(payload: CreateProductMasterPayload) {
    return apiClient<ProductMasterDetail>('/product-masters', { method: 'POST', body: payload });
  },

  /** Owner only. The change reaches every store that sells it. */
  update(id: string, payload: UpdateProductMasterPayload) {
    return apiClient<ProductMasterDetail>(`/product-masters/${id}`, { method: 'PATCH', body: payload });
  },

  /** Switch one master on / off at stores. A manager may name only assigned stores. */
  setLocations(id: string, payload: SetMasterLocationsPayload) {
    return apiClient<SetMasterLocationsResult>(`/product-masters/${id}/locations`, {
      method: 'POST',
      body: payload,
    });
  },

  /** Switch many masters on / off at stores in one transaction. */
  setLocationsBulk(payload: SetMasterLocationsPayload & { master_ids: string[] }) {
    return apiClient<SetMasterLocationsResult>('/product-masters/locations', {
      method: 'POST',
      body: payload,
    });
  },

  /** A store's own price in minor units; null goes back to the master price. */
  setStorePrice(id: string, locationId: string, price: number | null) {
    return apiClient<StorePriceResult>(`/product-masters/${id}/locations/${locationId}/price`, {
      method: 'PATCH',
      body: { price },
    });
  },

  /** 86 a dish at one outlet (its store row), or bring it back. Other outlets are untouched. */
  setMenuAvailability(productId: string, available: boolean) {
    return apiClient<unknown>(`/menu/items/${productId}/availability`, {
      method: 'PUT',
      body: { available },
    });
  },

  /** Owner only. Open conflicts unless `resolved: 'true'`. */
  listConflicts(params: {
    page?: number;
    limit?: number;
    conflict_type?: MasterConflictType;
    master_id?: string;
    resolved?: 'true' | 'false';
  } = {}) {
    return apiClientPaginated<MasterConflict[]>(`/product-masters/conflicts${buildQuery(params)}`);
  },

  /**
   * Owner only. Settle a conflict: the master's name / barcode or the store's;
   * add a store's missing variants or keep its set; match the master or keep
   * the store's row as a separate product (409 with a reason when it cannot move).
   */
  resolveConflict(id: string, action: ConflictAction) {
    return apiClient<MasterConflict & { action: ConflictAction }>(
      `/product-masters/conflicts/${id}/resolve`,
      { method: 'POST', body: { action } },
    );
  },

  /** CSV of every master in the import columns, plus `enabled_at` (the stores that sell it). */
  exportCsv() {
    return apiBlob('/product-masters/export');
  },
};
